import { createFeatureSelector, createSelector, select } from '@ngrx/store'

import { SeasonsState } from '@features/seasons/models/seasons.state'
import { SEASONS_FEATURE_TOKEN } from '@features/seasons/tokens/seasons.tokens'
import { CallingContext } from '@features/seasons/models/seasons.models'
import { Competition } from '@core/models/season-competition.models'
import { pipe, takeWhile } from 'rxjs'
import { filter, map } from 'rxjs/operators'
import { findSeasonCompetitionByCompetitionID } from '@core/utils/season.utils'
import { selectFavCompetitionId, selectFavTeamId, selectProfile } from '@core/store/selectors/user.selectors'

export const selectSeasonsFeature = createFeatureSelector<SeasonsState>(SEASONS_FEATURE_TOKEN)

export const selectNoCompetitions = createSelector(selectSeasonsFeature, (state) => !!state?.noCompetitions)

export const selectSeasonsContext = (callingContext: CallingContext) =>
  createSelector(selectSeasonsFeature, (state) => state[callingContext])

export const selectSeasonsByContext = (callingContext: CallingContext) =>
  createSelector(selectSeasonsContext(callingContext), (context) => context?.list)

export const selectCurrentSeasonByContext = (callingContext: CallingContext) =>
  createSelector(selectSeasonsContext(callingContext), (context) => context?.current)

export const selectActiveCompetition = (callingContext: CallingContext) =>
  createSelector(selectCurrentSeasonByContext(callingContext), selectFavCompetitionId, (season, competitionId) => {
    if (season) {
      return (season.competitions.find((comp) => comp.id === competitionId) || season.competitions[0]) as Competition
    }
  })

export const selectCurrentSeasonAndCompetition = (callingContext: CallingContext) =>
  createSelector(
    selectCurrentSeasonByContext(callingContext),
    selectActiveCompetition(callingContext),
    (season, competition) => {
      if (season) {
        return {
          season,
          competition
        }
      }
    }
  )

export const selectFavCurrentSeasonCompetitionWithFavTeamId = (callingContext: CallingContext) =>
  pipe(
    select(
      createSelector(
        selectCurrentSeasonAndCompetition(callingContext),
        selectFavTeamId,
        (seasonCompetition, teamId) => ({
          seasonCompetition,
          teamId
        })
      )
    ),
    map(({ seasonCompetition, teamId }) => ({ ...seasonCompetition, teamId })),
    takeWhile((fav) => !fav || (!!fav && (!fav.competition || !fav.season || !fav.teamId)), true),
    filter((fav) => !!fav && !!fav.competition && !!fav.season)
  )

export const selectCurrentSeasonCompetitionAndTeamIds = (callingContext: CallingContext) =>
  pipe(
    selectFavCurrentSeasonCompetitionWithFavTeamId(callingContext),
    map(({ season, competition, teamId }) => ({
      season,
      competition,
      teamId
    }))
  )

export const selectProfileWithFavCompetitionCheck = (callingContext: CallingContext) =>
  createSelector(selectProfile, selectCurrentSeasonByContext(callingContext), (profile, season) => {
    if (profile && season?.competitions?.length) {
      return {
        profile,
        favCompetitionNotExist: !season?.competitions.some((c) => c.id === profile.favouriteCompetitionId)
      }
    }
  })

export const selectFavSeasonCompetitionWithFallback = (callingContext: CallingContext) =>
  createSelector(
    selectProfile,
    selectSeasonsByContext(callingContext),
    selectCurrentSeasonAndCompetition(callingContext),
    (profile, seasons, seasonCompetition) => {
      if (!!profile && !!seasons && !!seasonCompetition) {
        const favData = findSeasonCompetitionByCompetitionID(seasons, profile?.favouriteCompetitionId)
        return favData ?? seasonCompetition
      }
    }
  )
